import { useEffect, useMemo, useState } from "react";
import { Box, Button, Flex, Slider, SliderFilledTrack, SliderThumb, SliderTrack, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import Cropper, { Area } from "react-easy-crop";
import { cropAvatarImage } from "./avatarCrop";

interface AvatarCropModalProps {
  file: File | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (file: File) => void;
}

export function AvatarCropModal({ file, isOpen, onClose, onConfirm }: AvatarCropModalProps) {
  const [t] = useTranslation();
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const imageUrl = useMemo(() => {
    if (!file) {
      return null;
    }
    return URL.createObjectURL(file);
  }, [file]);

  useEffect(() => {
    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [imageUrl]);

  useEffect(() => {
    if (!isOpen) {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      setIsSaving(false);
    }
  }, [isOpen]);

  if (!isOpen || !file || !imageUrl) {
    return null;
  }

  const handleConfirm = async () => {
    if (!croppedAreaPixels) {
      return;
    }

    setIsSaving(true);
    try {
      const croppedFile = await cropAvatarImage(imageUrl, croppedAreaPixels, file.name);
      onConfirm(croppedFile);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Flex
      align="center"
      bg="rgba(0, 0, 0, 0.72)"
      inset="0"
      justify="center"
      position="fixed"
      zIndex={1400}
      sx={{ backdropFilter: "blur(18px)" }}
    >
      <Box
        bg="#141517f2"
        borderRadius="12px"
        border="1px solid"
        borderColor="transparent.white.10"
        color="dune.dune"
        maxW="520px"
        p={6}
        w="calc(100vw - 32px)"
      >
        <Text fontSize="xl" fontWeight={700} letterSpacing="0.04em" mb={4} textTransform="uppercase">
          {t("screens.settings.cropAvatar")}
        </Text>

        <Box bg="neutral.900" height="360px" position="relative">
          <Cropper
            aspect={1}
            crop={crop}
            cropShape="round"
            image={imageUrl}
            objectFit="contain"
            onCropChange={setCrop}
            onCropComplete={(_croppedArea, nextCroppedAreaPixels) => setCroppedAreaPixels(nextCroppedAreaPixels)}
            onZoomChange={setZoom}
            showGrid={false}
            zoom={zoom}
          />
        </Box>

        <Box px={4} py={5}>
          <Slider aria-label="Avatar zoom" max={3} min={1} onChange={setZoom} step={0.01} value={zoom}>
            <SliderTrack bg="transparent.white.20">
              <SliderFilledTrack bg="transparent.white.50" />
            </SliderTrack>
            <SliderThumb />
          </Slider>
        </Box>

        <Flex gap={3}>
          <Button colorScheme="orange" isLoading={isSaving} onClick={handleConfirm}>
            {t("screens.settings.upload")}
          </Button>
          <Button
            onClick={onClose}
            bg="transparent"
            borderColor="transparent.white.20"
            color="dune.dune"
            variant="outline"
            _active={{ bg: "transparent.white.10", borderColor: "transparent.white.30" }}
            _hover={{ bg: "transparent.white.10", borderColor: "transparent.white.30" }}
          >
            {t("screens.settings.cancel")}
          </Button>
        </Flex>
      </Box>
    </Flex>
  );
}
